import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const SNAPP_BLUE = '#008EFA';

/** یک ردیف «قبل ← بعد» برای نمایش دقیق اثر عملیات. */
export interface SnappPayConfirmRow {
  label: string;
  before?: string;
  after?: string;
  /** اگر before/after ندارد، فقط یک مقدار ساده */
  value?: string;
  danger?: boolean;
}

export interface SnappPayConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  busy?: boolean;

  /** عنوان عملیات، مثلاً «حذف ردیف از سفارش اسنپ‌پی» */
  title: string;
  /** توضیح کامل اینکه دقیقاً چه اتفاقی می‌افتد */
  description: string;
  /** جزئیات عددی عملیات (مبلغ، تعداد، …) */
  rows?: SnappPayConfirmRow[];
  /** هشدار مرحلهٔ دوم — چرا برگشت‌ناپذیر است */
  irreversibleNote?: string;
  /** متن دکمهٔ نهایی */
  confirmLabel?: string;
}

/**
 * دیالوگ تأیید دومرحله‌ایِ عملیات اسنپ‌پی.
 *
 * چرا دومرحله‌ای: عملیات تغییر/لغو سفارش نزد اسنپ‌پی **برگشت‌ناپذیر** است
 * (الزام چک‌لیست اسنپ‌پی). مرحلهٔ اول دقیقاً می‌گوید چه چیزی عوض می‌شود و
 * مرحلهٔ دوم فقط تأیید نهایی می‌گیرد — بدون تایپ کردن چیزی، صرفاً یک تصمیم آگاهانه.
 *
 * فقط برای سفارش‌های اسنپ‌پی استفاده می‌شود؛ بقیهٔ سفارش‌ها مسیر عادی خودشان را دارند.
 */
const SnappPayConfirmDialog: React.FC<SnappPayConfirmProps> = ({
  open,
  onClose,
  onConfirm,
  busy = false,
  title,
  description,
  rows = [],
  irreversibleNote,
  confirmLabel = 'تأیید نهایی و اجرا',
}) => {
  const [step, setStep] = useState<1 | 2>(1);

  // با هر بار باز شدن، از مرحلهٔ اول شروع کن.
  useEffect(() => {
    if (open) setStep(1);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            component="img"
            src="/snapppay-logo.svg"
            alt="اسنپ‌پی"
            sx={{ width: 30, height: 30, borderRadius: '8px' }}
            onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800 }}>{title}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              عملیات روی تراکنش اسنپ‌پی — مرحلهٔ {step === 1 ? 'اول: بررسی' : 'دوم: تأیید نهایی'}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${step} از ۲`}
            sx={{ bgcolor: `${SNAPP_BLUE}1A`, color: SNAPP_BLUE, fontWeight: 700 }}
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {step === 1 ? (
          <>
            <Typography sx={{ fontSize: '0.85rem', lineHeight: 2, color: 'text.primary' }}>
              {description}
            </Typography>

            {rows.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              >
                {rows.map((r, i) => (
                  <Box
                    key={r.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      px: 1.5,
                      py: 1.1,
                      borderTop: i === 0 ? 'none' : '1px solid',
                      borderColor: 'divider',
                      bgcolor: i % 2 ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                      {r.label}
                    </Typography>
                    {r.value !== undefined ? (
                      <Typography
                        sx={{
                          fontSize: '0.82rem',
                          fontWeight: 700,
                          color: r.danger ? 'error.main' : 'text.primary',
                        }}
                      >
                        {r.value}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            color: 'text.disabled',
                            textDecoration: 'line-through',
                          }}
                        >
                          {r.before}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>←</Typography>
                        <Typography
                          sx={{
                            fontSize: '0.85rem',
                            fontWeight: 800,
                            color: r.danger ? 'error.main' : 'success.main',
                          }}
                        >
                          {r.after}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem', lineHeight: 1.9 }}>
              اسنپ‌پی بین هر دو تغییر سفارش حداقل ۳۰ ثانیه فاصله می‌خواهد. اگر همین حالا تغییر
              دیگری زده‌اید، سیستم تا پایان این فاصله اجازهٔ اجرا نمی‌دهد.
            </Alert>
          </>
        ) : (
          <>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ fontSize: '0.82rem', lineHeight: 2, fontWeight: 600 }}
            >
              {irreversibleNote ||
                'این عملیات روی تراکنش اسنپ‌پی انجام می‌شود و پس از اجرا به هیچ روشی قابل بازگشت نیست.'}
            </Alert>

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 2 }}>
              خلاصهٔ کاری که انجام می‌شود:
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mt: 0.5, lineHeight: 2 }}>
              {title}
            </Typography>
            {rows.length > 0 && (
              <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {rows.map((r) => (
                  <Chip
                    key={r.label}
                    size="small"
                    variant="outlined"
                    color={r.danger ? 'error' : 'default'}
                    label={`${r.label}: ${r.value ?? `${r.before} ← ${r.after}`}`}
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} disabled={busy} color="inherit" sx={{ fontWeight: 600 }}>
          انصراف
        </Button>
        {step === 1 ? (
          <Button
            onClick={() => setStep(2)}
            variant="contained"
            sx={{ bgcolor: SNAPP_BLUE, fontWeight: 700, '&:hover': { bgcolor: '#0077d6' } }}
          >
            متوجه شدم، ادامه
          </Button>
        ) : (
          <>
            <Button onClick={() => setStep(1)} disabled={busy} color="inherit" sx={{ fontWeight: 600 }}>
              بازگشت
            </Button>
            <Button
              onClick={onConfirm}
              disabled={busy}
              variant="contained"
              color="error"
              startIcon={<CheckCircleOutlineIcon />}
              sx={{ fontWeight: 700 }}
            >
              {busy ? 'در حال اجرا…' : confirmLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SnappPayConfirmDialog;
